import * as _ from "lodash";
import * as React from "react";

import useLocalStorage from "src/library/hooks/useLocalStorage";
import { ITag } from "src/modules/product-tags/api/product-tags.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import { useProductTagsApi } from "../api/product-tags.api";
import ProductTagFilter from "./ProductTagFilter";

interface ProductTagFiltersProps {
  handleSearch: (tags: { tags: { id?: { $in?: number[]; $notIn?: number[] } } }) => void;
}

const ProductTagFilters: React.ReactFCWithChildren<ProductTagFiltersProps> = ({ handleSearch }) => {
  const [ listTagsResponse, listTagsRequest ] = useProductTagsApi("LIST_PRODUCT_TAGS", {
    cache: {
      maxAge: cacheMinutesToMilliseconds(15),
      clearOnError: true
    }
  });

  const [ tags, setTags ] = useLocalStorage("product-tags", "[]");

  const productTags = React.useMemo(() => {
    try {
      return JSON.parse(tags || "[]") as ITag[];
    } catch (error) {
      return [];
    }
  }, [ tags ]);

  React.useEffect(() => {
    listTagsRequest({ filterBySettings: true });
  }, []);

  React.useEffect(() => {
    if (listTagsResponse.data) {
      setTags(JSON.stringify(listTagsResponse.data));
    }
  }, [ listTagsResponse.data ]);

  const [ tagFilters, setTagFilters ] = React.useState<{ $in?: number[]; $notIn?: number[] }>({});

  const allergens = React.useMemo(() => {
    if (!productTags) {
      return [];
    }

    return _.filter(productTags, tag => tag.type === "Allergen");
  }, [ productTags ]);

  const lifestyle = React.useMemo(() => {
    if (!productTags) {
      return [];
    }

    return _.filter(productTags, tag => tag.type === "Dietary & lifestyle");
  }, [ productTags ]);

  const handleTagsChange = React.useCallback(
    (type: "$in" | "$notIn", tags?: number[]) => {
      const newTagFilters = tagFilters;

      if (!(tags || []).length) {
        delete newTagFilters[ type ];
      } else {
        newTagFilters[ type ] = tags;
      }

      setTagFilters({ ...newTagFilters });

      if (_.isEmpty(newTagFilters)) {
        handleSearch({ tags: {} });
      } else {
        handleSearch({ tags: { id: newTagFilters } });
      }
    },
    [ tagFilters, handleSearch ]
  );

  return (
    <>
      {!!allergens.length && (
        <ProductTagFilter
          isLoading={listTagsResponse.pending}
          handleSearch={handleTagsChange}
          productTags={allergens}
          tagType="Allergens & Warnings"
        />
      )}

      {!!lifestyle.length && (
        <ProductTagFilter
          isLoading={listTagsResponse.pending}
          handleSearch={handleTagsChange}
          productTags={lifestyle}
          tagType="Dietary & lifestyle"
        />
      )}
    </>
  );
};

export default ProductTagFilters;
